/**
 * PÁGINA DE CAMBIO DE CONTRASEÑA (usuario ya logueado)
 *
 * Requiere sesión activa. Si no hay sesión, redirige a login.
 * Tras actualizar, hace handoff SSO a la app (returnTo).
 */

import React, { useState, FormEvent, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getBrandingConfig } from '@shared/config/brandingConfig';
import { supabase } from '@shared/lib/supabaseClient';
import { Button } from '@shared/ui/shadcn/ui/button';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { Input } from '@shared/ui/shadcn/ui/input';
import { Eye, EyeOff } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
import { createSsoTrace } from '@shared/utils/ssoTrace';
import { notifyCoreAPILogin } from '@shared/services/coreAuthService';
import { getDefaultReturnTo, resolveHandoffAppBaseUrl, APP_SSO_CONFIGS } from '@shared/navigation/appSSOConfig';
import { useDynamicBranding } from '../hooks/useDynamicBranding';

const getCoreApiUrl = (): string => {
  if (import.meta.env.VITE_CORE_API_URL?.trim()) {
    return import.meta.env.VITE_CORE_API_URL.trim();
  }
  return import.meta.env.DEV ? 'http://localhost:4000' : 'https://api.elfom.com';
};

const normalizeReturnTo = (returnToRaw: string | null, _app: string): string | null => {
  if (!returnToRaw) return null;
  try {
    if (returnToRaw.startsWith('http://') || returnToRaw.startsWith('https://')) {
      const u = new URL(returnToRaw);
      return (u.pathname || '/') + (u.search || '');
    }
  } catch {}
  if (returnToRaw.startsWith('/') && !returnToRaw.startsWith('//')) return returnToRaw;
  return null;
};

const handleHandoffRedirect = async (
  app: string,
  returnTo: string | null,
  onError: (message: string) => void,
  trace: ReturnType<typeof createSsoTrace>,
  clientOriginHint?: string | null,
): Promise<boolean> => {
  try {
    trace.step('SESSION_GET_START', {});
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) {
      trace.error('SESSION_GET_ERROR', { error: sessionError?.message });
      onError('No se pudo obtener la sesión. Intenta de nuevo.');
      return false;
    }
    if (!session.access_token) {
      trace.error('SESSION_INVALID', { reason: 'missing_access_token' });
      onError('Sesión inválida. Intenta de nuevo.');
      return false;
    }

    let normalizedApp = app?.trim() || null;
    if (!normalizedApp || !APP_SSO_CONFIGS[normalizedApp]) {
      trace.error('APP_NOT_SUPPORTED', { app: normalizedApp || 'null' });
      onError(`La aplicación "${normalizedApp || 'desconocida'}" no está soportada.`);
      return false;
    }

    const normalizedReturnTo = normalizeReturnTo(returnTo, normalizedApp);
    const defaultRoute = getDefaultReturnTo(normalizedApp);
    const safeReturnTo = (normalizedReturnTo && normalizedReturnTo !== '/')
      ? normalizedReturnTo
      : (defaultRoute || '/dashboard');

    const appBaseUrlResolved = resolveHandoffAppBaseUrl(normalizedApp, clientOriginHint);
    if (!appBaseUrlResolved) {
      trace.error('APP_ORIGIN_NOT_FOUND', { app: normalizedApp });
      onError(`No se pudo determinar la URL de destino para "${normalizedApp}".`);
      return false;
    }

    const coreApiUrl = getCoreApiUrl();
    const handoffBody: any = { app: normalizedApp };
    if (session.refresh_token) {
      handoffBody.session = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      };
    }
    if (safeReturnTo && safeReturnTo.startsWith('/')) {
      handoffBody.returnTo = safeReturnTo;
    }

    const handoffResponse = await fetch(`${coreApiUrl}/auth/handoff`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'X-Trace-Id': trace.traceId,
      },
      body: JSON.stringify(handoffBody),
    });

    if (!handoffResponse.ok) {
      const errorData = await handoffResponse.json().catch(() => ({ error: 'Error desconocido' }));
      trace.error('HANDOFF_POST_ERROR', { status: handoffResponse.status, error: errorData.error });
      onError(errorData.message || errorData.error || 'Error al crear código de acceso.');
      return false;
    }

    const handoffData = await handoffResponse.json();
    const { code } = handoffData;
    if (!code) {
      trace.error('HANDOFF_RESPONSE_NO_CODE', { response: handoffData });
      onError('No se recibió código de acceso.');
      return false;
    }

    const redirectUrl = `${appBaseUrlResolved}/auth/callback?code=${encodeURIComponent(code)}&returnTo=${encodeURIComponent(safeReturnTo)}&traceId=${encodeURIComponent(trace.traceId)}`;
    trace.step('REDIRECT_EXECUTE', {});
    window.location.href = redirectUrl;
    return true;
  } catch (err: any) {
    trace.error('HANDOFF_UNEXPECTED_ERROR', { error: err.message });
    onError('Error inesperado. Intenta de nuevo.');
    return false;
  }
};

const ChangePasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const app = params.get('app') ?? 'houseme';
  const returnTo = params.get('returnTo');
  const clientOriginParam = params.get('clientOrigin');

  useDynamicBranding(app);
  const branding = getBrandingConfig(app);
  const trace = createSsoTrace({ app, feature: 'CHANGE_PASSWORD' });

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hasSession, setHasSession] = useState<boolean | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setHasSession(true);
      } else {
        setHasSession(false);
      }
    };
    checkSession();
  }, []);

  // Sin sesión: redirigir a login (con el mismo returnTo para volver a la app)
  useEffect(() => {
    if (hasSession === false) {
      const loginUrl = `/login?app=${encodeURIComponent(app)}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}${
        clientOriginParam ? `&clientOrigin=${encodeURIComponent(clientOriginParam)}` : ''
      }`;
      navigate(loginUrl, { replace: true });
    }
  }, [hasSession, app, returnTo, clientOriginParam, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setLoading(false);
      return;
    }
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      trace.step('UPDATE_PASSWORD_START', {});
      const { error: updateError } = await supabase.auth.updateUser({ password });

      if (updateError) {
        trace.error('UPDATE_PASSWORD_ERROR', { error: updateError.message });
        setError(updateError.message || 'Error al actualizar contraseña');
        setLoading(false);
        return;
      }

      trace.step('UPDATE_PASSWORD_SUCCESS', {});

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        await notifyCoreAPILogin(session.access_token);
      }

      setSuccess(true);
      setLoading(false);

      setTimeout(async () => {
        const ok = await handleHandoffRedirect(
          app,
          returnTo,
          (msg) => {
            setError(msg);
            setSuccess(false);
          },
          trace,
          clientOriginParam,
        );
        if (!ok) {
          navigate(
            `/login?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}${
              clientOriginParam ? `&clientOrigin=${encodeURIComponent(clientOriginParam)}` : ''
            }`,
          );
        }
      }, 1500);
    } catch (err: any) {
      trace.error('UPDATE_PASSWORD_EXCEPTION', { error: err.message });
      setError('Error inesperado. Intenta de nuevo.');
      setLoading(false);
    }
  };

  if (hasSession === null) {
    return (
      <AuthLayout>
        <div className="w-full flex flex-col gap-6">
          <div className="text-center space-y-3">
            {app === 'houseme' && (
              <div className="flex items-center justify-center gap-2">
                <img src={branding.assets.logo || '/icon-houseme-192x192.png'} alt="HouseMe" className="h-8 w-8" />
                <span className="text-lg font-bold">
                  <span style={{ color: `hsl(${branding.colors.primary})` }}>House</span>
                  <span style={{ color: `hsl(${branding.colors.secondary || branding.colors.primary})` }}>Me</span>
                </span>
              </div>
            )}
            {app === 'empresa' && (
              <div className="flex items-center justify-center gap-2">
                <img src={branding.assets.logo || '/icon-192x192.png'} alt="ThePlatform360" className="h-8 w-8" />
                <span className="text-lg font-bold" style={{ color: `hsl(${branding.colors.primary})` }}>ThePlatform360</span>
              </div>
            )}
            <h1 className="text-xl font-semibold text-foreground">Comprobando sesión...</h1>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (hasSession === false) {
    return null;
  }

  return (
    <AuthLayout>
      <div className="w-full flex flex-col gap-6">
        <div className="text-center space-y-3">
          {app === 'houseme' ? (
            <div className="flex items-center justify-center gap-2">
              <img src={branding.assets.logo || '/icon-houseme-192x192.png'} alt="HouseMe" className="h-8 w-8" />
              <span className="text-lg lg:text-xl font-bold">
                <span style={{ color: `hsl(${branding.colors.primary})` }}>House</span>
                <span style={{ color: `hsl(${branding.colors.secondary || branding.colors.primary})` }}>Me</span>
              </span>
            </div>
          ) : app === 'empresa' ? (
            <div className="flex items-center justify-center gap-2">
              <img src={branding.assets.logo || '/icon-192x192.png'} alt="ThePlatform360" className="h-8 w-8" />
              <span className="text-lg lg:text-xl font-bold" style={{ color: `hsl(${branding.colors.primary})` }}>
                ThePlatform360
              </span>
            </div>
          ) : branding.assets.logo ? (
            <img src={branding.assets.logo} alt={branding.label} className="h-12 mx-auto" />
          ) : null}
          <h1 className="text-xl font-semibold text-foreground">Cambiar contraseña</h1>
          <p className="text-sm text-muted-foreground">Elige una contraseña segura de al menos 6 caracteres.</p>
        </div>

        {success ? (
          <Alert>
            <AlertDescription className="text-center">
              Contraseña actualizada. Redirigiendo...
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    label="Nueva contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Mínimo 6 caracteres"
                    className="pr-10"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirmar contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Repite la contraseña"
                    className="pr-10"
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full" size="lg">
                {loading ? 'Actualizando...' : 'Actualizar contraseña'}
              </Button>
            </form>

            <div className="mt-4 text-center text-xs text-muted-foreground">
              <button
                type="button"
                onClick={() => navigate(`/login?app=${app}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`)}
                className="text-primary hover:underline font-medium"
              >
                Volver al inicio de sesión
              </button>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  );
};

export default ChangePasswordPage;
